import React from "react";
import { Routes, Route, Navigate, BrowserRouter } from "react-router-dom";
import { ApolloProvider } from "@apollo/client";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { apolloClient } from "./utils/apollo-client";
import EmployeeList from "./components/Employees/EmployeeList";
import EmployeeForm from "./components/Employees/EmployeeForm";
import LocationList from "./components/Locations/LocationList";
import LocationForm from "./components/Locations/LocationForm";
import Login from "./components/Auth/Login";
import ProtectedRoute from "./components/Auth/ProtectedRoute";
import { Helmet } from "react-helmet-async";
import DashboardPage from "./components/Dashboard/DashboardPage";
import DashboardLayout from "./components/Layout/DashboardLayout";
import FormList from "./components/Form/FormList";
import CreateForm from "./components/Form/CreateForm";
import FormSubmissionList from "./components/Submission/FormSubmissionList";
import CreateFormSubmission from "./components/Submission/CreateFormSubmission";
import EditFormSubmission from "./components/Submission/EditFormSubmission";
import PrintableSubmission from "./components/Submission/PrintableSubmission";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import Header from "./components/Layout/Header";
import Footer from "./components/Layout/Footer";
import HomePage from "./pages/HomePage";
import { css, Global } from "@emotion/react";
import AboutPage from "./pages/AboutPage";
import ServicesPage from "./pages/ServicesPage";
import FacilitiesPage from "./pages/FacilitiesPage";
import FacilityDetailPage from "./pages/FacilityDetailPage";
import TestimonialsPage from "./pages/TestimonialsPage";
import ContactPage from "./pages/ContactPage";
import { TimesheetEntryForm } from "./components/Timesheet/TimesheetEntryForm";
import { TimesheetView } from "./components/Timesheet/TimesheetView";
import TimesheetDashboard from "./components/Timesheet/TimesheetDashboard";
import PayrollDashboard from "./components/Payroll/PayrollDashboard";
import EmployeeTimesheets from "./components/Timesheet/EmployeeTimesheets";
import PayrollRunList from "./components/Payroll/PayrollRunList";
import PayStubView from "./components/Payroll/PayStubView";
import ScrollToHashElement from "@cascadia-code/scroll-to-hash-element";

const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#1976d2",
    },
    secondary: {
      main: "#dc004e",
    },
    background: {
      default: "#f5f5f5",
    },
  },
  typography: {
    h4: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
});

const hometheme = css`
  @tailwind base;
  @tailwind components;
  @tailwind utilities;
`;

const AppRoutes: React.FC = () => {
  console.log("AppRoutes");
  console.log(location.pathname);
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <div>Loading...</div>
      </div>
    );
  }

  const isAuthenticated = !!user;
  console.log("isAuthenticated", isAuthenticated);

  return (
    <div className="App">
      <ScrollToHashElement />
      <Helmet>
        <title>Almond Tree - Home</title>
      </Helmet>

      {/* Landing Pages with Header/Footer */}
      <div className="min-h-screen bg-white">
        <Header />
        <main>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/facilities" element={<FacilitiesPage />} />
            <Route
              path="/facilities/:facilityId"
              element={<FacilityDetailPage />}
            />
            <Route path="/testimonials" element={<TestimonialsPage />} />
            <Route path="/contact" element={<ContactPage />} />

            {/* Dashboard Routes (uses DashboardLayout with AdminHeader) */}
            <Route
              path="/dashboard/login"
              element={
                isAuthenticated ? (
                  <Navigate to="/dashboard" replace />
                ) : (
                  <Login />
                )
              }
            />

            {/* Main protected routes with DashboardLayout */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              {/* Index route */}
              <Route index element={<DashboardPage />} />

              {/* Employee routes - protected with Admin group */}
              <Route
                path="/dashboard/employees"
                element={
                  <ProtectedRoute requiredGroup="Admin">
                    <EmployeeList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/employees/new"
                element={
                  <ProtectedRoute requiredGroup="Admin">
                    <EmployeeForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/employees/edit/:id"
                element={
                  <ProtectedRoute requiredGroup="Admin">
                    <EmployeeForm />
                  </ProtectedRoute>
                }
              />

              {/* Location routes - protect these too if needed */}
              <Route
                path="/dashboard/locations"
                element={
                  <ProtectedRoute requiredGroup="Admin">
                    <LocationList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/locations/new"
                element={
                  <ProtectedRoute requiredGroup="Admin">
                    <LocationForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/locations/edit/:id"
                element={
                  <ProtectedRoute requiredGroup="Admin">
                    <LocationForm />
                  </ProtectedRoute>
                }
              />

              {/* Form routes - protect these too if needed */}
              <Route
                path="/dashboard/forms"
                element={
                  <ProtectedRoute requiredGroup="Admin">
                    <FormList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/forms/new"
                element={
                  <ProtectedRoute requiredGroup="Admin">
                    <CreateForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/forms/edit/:id"
                element={
                  <ProtectedRoute requiredGroup="Admin">
                    <CreateForm />
                  </ProtectedRoute>
                }
              />

              {/* Submission routes - accessible to all authenticated users */}
              <Route
                path="/dashboard/submissions"
                element={
                  <ProtectedRoute>
                    <FormSubmissionList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/submissions/new/:formId"
                element={
                  <ProtectedRoute>
                    <CreateFormSubmission />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/submissions/edit/:submissionId"
                element={
                  <ProtectedRoute requiredGroup="Admin">
                    <EditFormSubmission />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/submissions/print/:submissionId"
                element={
                  <ProtectedRoute>
                    <PrintableSubmission />
                  </ProtectedRoute>
                }
              />

              {/* Timesheet Routes - Accessible to all employees */}
              <Route
                path="/dashboard/timesheets"
                element={
                  <ProtectedRoute>
                    <TimesheetDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/timesheets/my-timesheet"
                element={
                  <ProtectedRoute>
                    <TimesheetView />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/timesheets/entry"
                element={
                  <ProtectedRoute>
                    <TimesheetEntryForm />
                  </ProtectedRoute>
                }
              />

              {/* Admin-only Timesheet Routes */}
              <Route
                path="/dashboard/timesheets/employee-timesheets"
                element={
                  <ProtectedRoute requiredGroup="Admin">
                    <EmployeeTimesheets />
                  </ProtectedRoute>
                }
              />

              {/* Payroll Routes - Admin only */}
              <Route
                path="/dashboard/payroll"
                element={
                  <ProtectedRoute requiredGroup="Admin">
                    <PayrollDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/payroll/runs"
                element={
                  <ProtectedRoute requiredGroup="Admin">
                    <PayrollRunList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/payroll/paystub/:payrollRunId"
                element={
                  <ProtectedRoute>
                    <PayStubView />
                  </ProtectedRoute>
                }
              />
            </Route>
            {/* Catch all route - redirect to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
        <Footer />
      </div>
    </div>
  );
};

function App() {
  return (
    <ApolloProvider client={apolloClient}>
      <ThemeProvider theme={theme}>
        <Global styles={hometheme} />
        <CssBaseline />
        <BrowserRouter>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <AuthProvider>
              {/* <TimesheetProvider> */}
              {/* <PayrollProvider> */}
              <AppRoutes />
              {/* </PayrollProvider> */}
              {/* </TimesheetProvider> */}
            </AuthProvider>
          </LocalizationProvider>
        </BrowserRouter>
      </ThemeProvider>
    </ApolloProvider>
  );
}

export default App;
